<template>
    <div>
        <form v-if="IsEditor" class="admin-control widget-admin">
            <div v-if="!Choosing">
                <button class="btn btn-sm btn-outline-dark me-1" type="button" @click.prevent="ShowConfig = !ShowConfig">
                    <i class="fa-regular fa-gear"></i>
                </button>
                <button class="btn btn-sm btn-success me-1" v-show="Dirty || CulledPeople.length !== OriginalPeople.length"
                        type="button" @click.prevent="SaveChanges">Save People</button>
                <button class="btn btn-sm btn-danger me-1" v-show="Dirty" 
                        type="button" @click.prevent="RevertChanges">Revert Changes</button>
                <button class="btn btn-sm btn-primary me-1" v-if="!ShowConfig" 
                        type="button" @click="Choosing = true">Add Person</button>
            </div>
            <div v-show="ShowConfig" class="widget-config card">
                <div class="card-body row">
                    <div class="col-sm-4">
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" v-model="Config.IncludeImage" /> Show Photo
                            </label>
                        </div>
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" v-model="Config.IncludeEmail" /> Show Email
                            </label>
                        </div>
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" v-model="Config.IncludePhone" /> Show Phone Number
                            </label>
                        </div>
                    </div>
                    <div class="col-sm-4">
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" v-model="Config.IncludeAltPhone" /> Show Alternate Phone Number
                            </label>
                        </div>
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" v-model="Config.IncludeFax" /> Show Fax Number
                            </label>
                        </div>
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" v-model="Config.IncludeTitle" /> Show Title
                            </label>
                        </div>
                    </div>
                    <div class="col-sm-4">
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" v-model="Config.IncludeBio" /> Show Bio
                            </label>
                        </div>
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" v-model="Config.IncludeTwitter" /> Show X/Twitter
                            </label>
                        </div>
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" v-model="Config.IncludeLinkedIn" /> Show LinkedIn
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="IsEditor" class="admin-control add-person" :class="{ choosing: Choosing }">
                <div>
                    <div class="alert alert-danger text-center" role="alert" v-if="OriginalPeople.length !== CulledPeople.length">
                        One or more people have been removed from this list. Resave to fix.
                    </div>
                </div>
                <div v-show="Choosing">
                    <div class="flex-grow-1">
                        <select2 :options="options" :config="{ width: '100%' }" @input="tempId = $event">
                            <option value="">Select person...</option>
                        </select2>
                    </div>
                    <div class="ms-2">
                        <button class="btn btn-sm btn-success me-1" v-if="tempId" type="button" @click="ChoosePerson">Add</button>
                        <button class="btn btn-sm btn-danger me-1" type="button" @click="Choosing = false">Cancel</button>
                    </div>
                    <p v-if="IsAdmin"><a :href="CreatePersonUrl" target="_blank">Create a new person</a></p>
                </div>
            </div>
        </form>

        <draggable v-if="IsEditor" class="contacts" :list="People" item-key="Id" handle=".drag-contact" animation="250">
            <template #item="{ element: person, index }">
                <contact v-bind="person" @remove="RemovePerson(person)" :config="Config" @popmodal="popModal(person.Id)"></contact>
            </template>
        </draggable>
        <div v-if="!IsEditor" class="contacts">
            <contact v-for="person in People" v-bind="person" :key="person.Id" :config="Config" @popmodal="popModal(person.Id)"></contact>
        </div>

        <Teleport to="body">
            <bio-modal v-for="person in People" :ref="el => setModalRef(el, person.Id)" v-bind="person" :key="person.Id"
                       :config="Config"></bio-modal>
        </Teleport>
    </div>
</template>

<script setup>
    import { ref, reactive, computed, inject, onMounted, onBeforeUnmount, useTemplateRef } from 'vue';
    import Contact from './people-widget-contact.vue';
    import BioModal from './people-widget-bio-modal.vue';
    import Select2 from '../select2.vue';
    import Draggable from 'vuedraggable';
    import 'messenger';
    /*import { Modal } from 'bootstrap';*/

    const Modal = window.bootstrap?.Modal;

    const IsAdmin = inject('IsAdmin');
    const IsEditor = inject('IsEditor');
    const Widget = inject('Widget');
    const CreatePersonUrl = inject('CreatePersonUrl');
    const UpdateWidgetUrl = inject('UpdateWidgetUrl');
    const AllPeople = inject('AllPeople');

    const Config = ref(null);
    const OriginalConfig = ref(null);
    const People = ref([]);
    const OriginalPeople = ref(null);
    const CulledPeople = ref([]);

    const ShowConfig = ref(false);
    const Choosing = ref(false);
    const tempId = ref(null);
    const bioModals = ref({});

    Config.value = _.cloneDeep(Widget.Config);
    OriginalConfig.value = _.cloneDeep(Widget.Config);
    OriginalPeople.value = _.cloneDeep(Widget.PeopleIds);

    LoadPeople()

    const Dirty = computed(() => {
        // config changed?
        let configChanged = false

        $.each(Config.value, (key, value) => {
            if (key !== 'Properties' && OriginalConfig.value[key] !== value) {
                configChanged = true
                return false
            }
        })

        if (configChanged) return true

        // people changed?
        if (People.value.length !== CulledPeople.value.length)
            return true

        for (let i = 0; i < People.value.length; i++) {
            if (People.value[i].Id !== CulledPeople.value[i])
                return true
        }

        return false
    })

    const options = computed(() => {
        return $.map(AllPeople, person => {
            const opt = {
                id: person.Id,
                text: person.FullName
            }

            if (People.value.some(p => p.Id === person.Id)) {
                opt.disabled = true
            }

            return opt
        })
    })

    function LoadPeople() {
        for (let i = 0; i < OriginalPeople.value.length; i++) {
            AddPerson(OriginalPeople.value[i], true)
        }
    }

    function ChoosePerson() {
        AddPerson(tempId.value, false)
        tempId.value = null
        Choosing.value = false
    }

    function AddPerson(id, initial) {
        let info = null

        for (let i = 0; i < AllPeople.length; i++) {
            const p = AllPeople[i]
            if (p.Id === id) {
                info = p
                break
            }
        }

        if (!info) return

        const newPerson = $.extend(true, {}, info)

        People.value.push(newPerson)

        if (initial) {
            CulledPeople.value.push(newPerson.Id)
        }
    }

    function RemovePerson(person) {
        People.value.splice(People.value.indexOf(person), 1)
    }

    function SaveChanges() {
        const peopleIds = _.map(People.value, p => p.Id)

        Messenger().run({
            action: $.ajax,
            successMessage: 'Changes have been saved',
            errorMessage: 'There was an error saving.',
            progressMessage: 'Saving...'
        }, {
            method: 'PUT',
            url: UpdateWidgetUrl,
            data: {
                WidgetId: Widget.WidgetId,
                WidgetTypeId: Widget.WidgetTypeId,
                PageId: Widget.PageId,
                SectionId: Widget.SectionId,
                PeopleIds: peopleIds,
                Config: Config.value
            },
            success() {
                const originalPeopleIds = $.map(People.value, p => p.Id)

                OriginalPeople.value = _.cloneDeep(originalPeopleIds)
                CulledPeople.value = _.cloneDeep(originalPeopleIds)
                OriginalConfig.value = _.cloneDeep(Config.value)
            }
        })
    }

    function RevertChanges() {
        Config.value = _.cloneDeep(OriginalConfig.value)
        People.value = []
        CulledPeople.value = []
        LoadPeople()
    }

    function setModalRef(el, id) {
        if (el) {
            bioModals.value[id] = el;
        } else {
            delete bioModals.value[id];
        }
    }

    function popModal(id) {
        const comp = bioModals.value[id];
        if (comp) {
            const modal = Modal.getOrCreateInstance(comp.$el);
            modal.show();
        }
    }

    function beforeUnloadHandler(e) {
        if (Dirty.value) {
            return 'There are unsaved changes, are you sure you want to leave?'
        }
    }

    onMounted(() => {
        $(window).trigger('widget-loaded')

        if (IsEditor) {
            $(window).on('beforeunload', beforeUnloadHandler)
        }
    })

    onBeforeUnmount(() => {
        $(window).off('beforeunload', beforeUnloadHandler)
    })
</script>